import { ArrowLeftOutlined, SignatureFilled } from '@ant-design/icons';
import {
  getGetFrontRichiesteIdAllegatiQueryKey,
  getGetFrontRichiesteIdQueryKey,
} from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import {
  getGetFrontRichiesteIdRiepilogoDownloadQueryKey,
  usePutFrontRichiesteIdRiepilogo,
} from '@framework/api-client/api/inquiries-compile/riepilogo-richiesta/riepilogo-richiesta.ts';
import { type AllegatoRichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/allegatoRichiestaDTO.ts';
import { type DettaglioFirma } from '@framework/api-client/api/inquiries-compile/schemas/dettaglioFirma.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { SummaryDetailCard } from '@framework/ui/components/summary/SummaryDetailCard.tsx';
import { Button, Flex, message } from 'antd';
import { type RcFile } from 'antd/es/upload';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { DownloadSummaryButton } from '../download-pdf/DownloadSummaryButton.tsx';
import { ActionButtonContainer } from '../signer/ActionButtonContainer.tsx';
import { ActionMessage } from '../signer/ActionMessage.tsx';
import { SignerHeader } from '../signer/SignerHeader.tsx';
import { SignerPanel } from '../signer/SignerPanel.tsx';
import { SignatureModalButton } from '../signer/signature/SignatureModalButton.tsx';
import { SummarySignerReadonlyView } from './SummarySignerReadonlyView.tsx';
import { useSignedSummaryFile } from './useSignedSummaryFile.ts';

export interface ISummarySignerEditableViewProps {
  submissionId: string;
  uploadEnabled: boolean;
  /**
   * Estensioni file accettate per l'upload del riepilogo firmato.
   * `null` indica nessun filtro sui formati (es. modalità "digitale o autografa").
   */
  acceptedFileExtensions?: string | null;
  canInteract: boolean;
  hasSignedSummary: boolean;
  signedSummaryAttachment?: AllegatoRichiestaDTO | null;
  signatures?: DettaglioFirma[];
}

/**
 * Componente per la visualizzazione e gestione del riepilogo firmato.
 */
export function SummarySignerEditableView(
  props: Readonly<ISummarySignerEditableViewProps>,
) {
  const {
    submissionId,
    uploadEnabled,
    acceptedFileExtensions,
    canInteract,
    hasSignedSummary,
    signedSummaryAttachment,
    signatures,
  } = props;
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [file, setFile] = useState<RcFile | null>(null);
  const [forceShowUpload, setForceShowUpload] = useState(false);
  const uploadMutation = usePutFrontRichiesteIdRiepilogo();

  const signedFile = useSignedSummaryFile(
    hasSignedSummary,
    signedSummaryAttachment,
  );

  const handleUploadAndComplete = useCallback(async () => {
    if (!(file && submissionId)) return;

    try {
      await uploadMutation.mutateAsync({
        id: submissionId,
        data: { file },
      });

      message.success(
        t(
          'riepilogo-firmato.upload-completato',
          'Riepilogo firmato caricato con successo',
        ),
      );
      setFile(null);
      setForceShowUpload(false);

      await queryClient.invalidateQueries(
        getGetFrontRichiesteIdRiepilogoDownloadQueryKey(submissionId),
      );
      await queryClient.invalidateQueries(
        getGetFrontRichiesteIdQueryKey(submissionId),
      );
      await queryClient.invalidateQueries(
        getGetFrontRichiesteIdAllegatiQueryKey(submissionId),
      );
    } catch (e) {
      console.error('[SummarySignerEditableView] Upload error: ', { error: e });
      message.error(
        getNetworkErrorMessage(
          e,
          t(
            'riepilogo-firmato.errore-in-upload',
            'Si è verificato un problema durante il caricamento del riepilogo firmato',
          ),
        ),
      );
    }
  }, [file, submissionId, uploadMutation, queryClient, t]);

  if (!uploadEnabled) {
    return (
      <SummarySignerReadonlyView
        submissionId={submissionId}
        hasSignedSummary={false}
        disabled={!canInteract}
      />
    );
  }

  if (hasSignedSummary && !forceShowUpload) {
    return (
      <SummaryDetailCard variant="signer">
        <SignerHeader
          icon={<SignatureFilled aria-hidden="true" />}
          title={t(
            'riepilogo-firmato.procedura-firma-riepilogo',
            'Procedura di Firma del Riepilogo della Richiesta',
          )}
        />
        <ActionButtonContainer
          message={
            <ActionMessage
              message={t(
                'riepilogo-firmato.riepilogo-firmato-caricato',
                "Il riepilogo firmato è stato caricato. È possibile procedere con l'invio della richiesta.",
              )}
              type="info"
            />
          }
          button={
            <Flex align="center" gap={8}>
              <Button
                block
                size="large"
                onClick={() => setForceShowUpload(true)}
              >
                <ArrowLeftOutlined aria-hidden="true" />{' '}
                {t(
                  'riepilogo-firmato.annulla-e-ricarica-file-firmato',
                  'Annulla e Ricarica file firmato',
                )}
              </Button>
              <SignatureModalButton
                signedFile={signedFile}
                signatures={signatures}
                type="default"
                size="large"
              >
                {t('signer.verifica-firma', 'Verifica Firma')}
              </SignatureModalButton>
            </Flex>
          }
        />
      </SummaryDetailCard>
    );
  }

  return (
    <SummaryDetailCard variant="signer">
      <SignerPanel
        title={t(
          'riepilogo-firmato.procedura-firma-riepilogo',
          'Procedura di Firma del Riepilogo della Richiesta',
        )}
        description={t(
          'riepilogo-firmato.scarica-firma-ricarica-desc',
          'Scaricare il documento di Riepilogo della Richiesta, procedere alla firma digitale e ricaricare il documento firmato.',
        )}
        downloadButton={
          <DownloadSummaryButton
            submissionId={submissionId}
            signed={!forceShowUpload && !!signedFile}
            disabled={!canInteract}
            ignoreMobile
            forceIcon
          />
        }
        uploadName={`upload-signed-summary-${submissionId}`}
        accept={acceptedFileExtensions}
        file={file}
        onFileSelect={setFile}
        onUpload={handleUploadAndComplete}
        disabled={!canInteract}
        isUploading={uploadMutation.isLoading}
        signedFile={forceShowUpload ? null : signedFile}
        signatures={signatures}
      />
    </SummaryDetailCard>
  );
}
